import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Trophy, ArrowDownLeft, ArrowUpRight, Sparkles } from "lucide-react";
import { formatUsd } from "../lib/currencyDisplay";

export default function Loyalty() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/loyalty/me`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load rewards");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 text-[var(--zc-text-2)]" data-testid="loyalty-loading">
        Loading your ZCT balance…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="loyalty-error">
        <div className="eyebrow mb-2">Rewards</div>
        <h1 className="zc-headline text-4xl">ZCT Loyalty</h1>
        <p className="text-[var(--zc-text-2)] mt-4">{error}</p>
      </div>
    );
  }

  const balance = data?.loyalty?.zct_balance || 0;
  const dollarValue = balance / (data?.redeem_rate || 100);
  const earnMult = data?.earn_multiplier || 1;
  const earnRate = data?.earn_rate || 1;
  const tripsPastYear = data?.trips_past_year ?? 0;
  const tripsToTier2 = data?.trips_to_tier2 ?? 5;
  const tierRequired = data?.tier_trips_required || 5;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="loyalty-page">
      <div className="eyebrow mb-2">Rewards</div>
      <h1 className="zc-headline text-4xl md:text-5xl">ZCT Loyalty</h1>
      <p className="text-[var(--zc-text-2)] mt-3 max-w-xl">
        Z-Reward (ZCT) is off-chain only — $0 gas. Earn on trips, redeem toward bookings.
        {data?.expiry_enabled && data?.expiry_months
          ? ` Unused earn balances expire ${data.expiry_months} months after issuance.`
          : ""}
      </p>

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        <div className="zc-card p-6 md:col-span-2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #FFFFFF 0%, var(--zc-accent-soft) 100%)" }}>
          <div className="absolute right-0 bottom-0 opacity-[0.06] text-[var(--zc-accent)]"><Trophy size={140} /></div>
          <div className="eyebrow mb-3">Current balance</div>
          <div className="zc-stat text-6xl text-[var(--zc-accent)]" data-testid="loyalty-balance">{balance.toLocaleString()}</div>
          <div className="eyebrow mt-2">ZCT · ≈ ${formatUsd(dollarValue)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 max-w-md">
            <div>
              <div className="eyebrow mb-1">Earned</div>
              <div className="zc-stat text-xl mt-1">{data?.loyalty?.lifetime_earned || 0}</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Redeemed</div>
              <div className="zc-stat text-xl mt-1">{data?.loyalty?.lifetime_redeemed || 0}</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Rate</div>
              <div className="zc-title text-xl mt-1">{earnMult}×</div>
              <div className="zc-meta mt-0.5">{earnRate} ZCT / $1</div>
            </div>
          </div>
        </div>

        <div className="zc-card p-6">
          <div className="flex items-center gap-2 eyebrow mb-3"><Sparkles size={12} /> How to earn</div>
          <ul className="space-y-2 text-sm text-[var(--zc-text)]">
            <li>· {earnRate} ZCT per $1 spent on bookings ({earnMult}× tier)</li>
            <li>· Complete {tierRequired}+ trips in a year for 2× rewards ({tripsPastYear}/{tierRequired} so far)</li>
            {tripsToTier2 > 0 && (
              <li className="text-[var(--zc-text-2)]">· {tripsToTier2} more completed trip{tripsToTier2 === 1 ? "" : "s"} to unlock 2×</li>
            )}
            <li>· 100 ZCT signup bonus</li>
            <li>· Hosts earn 2 ZCT / $1 on completed trips</li>
            <li>· Redeem up to 50% of any booking</li>
          </ul>
        </div>
      </div>

      <div className="mt-10">
        <div className="eyebrow mb-3">Activity</div>
        <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table" data-testid="loyalty-ledger">
          <div className="hidden md:grid md:grid-cols-[24px,1fr,auto] zc-stack-head items-center gap-3 px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
            <div></div><div>Activity</div><div className="text-right">Amount</div>
          </div>
          {data?.ledger?.length === 0 && <div className="px-4 sm:px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No activity yet.</div>}
          {data?.ledger?.map(l => (
            <div key={l.ledger_id} className="zc-stack-row md:grid md:grid-cols-[24px,1fr,auto] md:items-center gap-3 px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] last:border-0 text-sm">
              <div data-label="">
                {l.type === "earn"
                  ? <ArrowDownLeft size={14} className="text-[var(--zc-success)]" />
                  : <ArrowUpRight size={14} className="text-[var(--zc-text-2)]" />}
              </div>
              <div data-label="Activity">
                <div className="text-[var(--zc-text)] capitalize font-semibold">{l.type}</div>
                <div className="text-xs text-[var(--zc-text-3)]">{l.reason}</div>
              </div>
              <div data-label="Amount" className={"font-semibold md:text-right " + (l.amount > 0 ? "text-[var(--zc-success)]" : "text-[var(--zc-text-2)]")}>
                {l.amount > 0 ? "+" : ""}{l.amount} ZCT
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
